import Heading from 'LuxKit/Typography/Heading'
import { rem } from 'polished'
import * as React from 'react'
import styled from 'styled-components'
import LayoutContainer from 'components/Common/LayoutContainer/LayoutContainer'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TripLandingPageTrendingDestinationsCarousel from './TripLandingPageTrendingDestinationsCarousel'
import { useTrendingDestinations } from './useTripLandingPageTrendingDestinations'
import type { TrendingDestination } from 'home/components/TrendingDestinations/constants'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'

const SectionContainer = styled(VerticalSpacer)`
  padding-top: ${rem(64)};
  padding-bottom: ${rem(40)};
`

export interface CityTrendingDestination extends TrendingDestination {
  countryCode?: string
  displayName?: string
}

type DestinationMessageKey = 'canggu' | 'phuket' | 'seminyak' | 'ubud' | 'goldCoast' | 'sydney' | 'singapore' | 'melbourne' | 'dubai'

const destinationNameMessages = defineMessages({
  canggu: {
    id: 'gi7PlzFJDgJR-tZ8hxDN',
    defaultMessage: 'Canggu',
    description: 'Fallback trip planner destination label for Canggu on the landing page destination carousel',
  },
  phuket: {
    id: 'QTnJhuH3s9PnoiCpixTi',
    defaultMessage: 'Phuket',
    description: 'Fallback trip planner destination label for Phuket on the landing page destination carousel',
  },
  seminyak: {
    id: '4SrOXabo5mxCfI30te67',
    defaultMessage: 'Seminyak',
    description: 'Fallback trip planner destination label for Seminyak on the landing page destination carousel',
  },
  ubud: {
    id: 'vYL1MaQ7O_U7P1VsYl4i',
    defaultMessage: 'Ubud',
    description: 'Fallback trip planner destination label for Ubud on the landing page destination carousel',
  },
  goldCoast: {
    id: 'yMBRRvGFQ0cyKqrK4TJT',
    defaultMessage: 'Gold Coast',
    description: 'Fallback trip planner destination label for Gold Coast on the landing page destination carousel',
  },
  sydney: {
    id: 'VGeyrJ9siJlMx9av9uXm',
    defaultMessage: 'Sydney',
    description: 'Fallback trip planner destination label for Sydney on the landing page destination carousel',
  },
  singapore: {
    id: '8_8tnL_tdo5jB99WKS_V',
    defaultMessage: 'Singapore',
    description: 'Fallback trip planner destination label for Singapore on the landing page destination carousel',
  },
  melbourne: {
    id: 's02yTKqUXzbixJTXOaDb',
    defaultMessage: 'Melbourne',
    description: 'Fallback trip planner destination label for Melbourne on the landing page destination carousel',
  },
  dubai: {
    id: 'gu_HtgBpu8UWTMM4RDcC',
    defaultMessage: 'Dubai',
    description: 'Fallback trip planner destination label for Dubai on the landing page destination carousel',
  },
})

type FallbackDestination = CityTrendingDestination & {
  displayNameKey: DestinationMessageKey
}

export const destinations: Array<FallbackDestination> = [
  {
    destinationId: 'le_425f0cea87a40c5a8860e3db7c918c4e',
    imageId: 'hotel-destination-canggu',
    name: 'Canggu',
    displayNameKey: 'canggu',
    url: '/search/hotels?destinationId=le_7cba87f571a31baf11325f462fbdcf14&destinationName=Canggu',
    countryCode: 'ID',
  },
  {
    destinationId: 'le_d47bb339212ddfab0fadd99d492a5fc3',
    imageId: 'hotel-destination-phuket',
    name: 'Phuket',
    displayNameKey: 'phuket',
    url: '/search/hotels?destinationId=le_7cba87f571a31baf11325f462fbdcf14&destinationName=Phuket',
    countryCode: 'TH',
  },
  {
    destinationId: 'le_790d9f1f52e10ef90ede8efcf7f3c078',
    imageId: 'hotel-destination-seminyak',
    name: 'Seminyak',
    displayNameKey: 'seminyak',
    url: '/search/hotels?destinationId=le_7cba87f571a31baf11325f462fbdcf14&destinationName=Seminyak',
    countryCode: 'ID',
  },
  {
    destinationId: 'le_7f7ed8ecfca9e17696ff654508efd86a',
    imageId: 'hotel-destination-ubud',
    name: 'Ubud',
    displayNameKey: 'ubud',
    url: '/search/hotels?destinationId=le_7cba87f571a31baf11325f462fbdcf14&destinationName=Ubud',
    countryCode: 'ID',
  },
  {
    destinationId: 'le_bac732346917b0a1231354ef55faf00a',
    imageId: 'krvovndggw4zalo1kgo8',
    name: 'Gold Coast',
    displayNameKey: 'goldCoast',
    url: '/search/hotels?destinationId=le_7cba87f571a31baf11325f462fbdcf14&destinationName=Gold Coast',
    countryCode: 'AU',
  },
  {
    destinationId: 'le_678c61ac5b4f91ca86e112fc50e63766',
    imageId: 'hotel-destination-sydney',
    name: 'Sydney',
    displayNameKey: 'sydney',
    url: '/search/hotels?destinationId=le_7cba87f571a31baf11325f462fbdcf14&destinationName=Sydney',
    countryCode: 'AU',
  },
  {
    destinationId: 'le_bd4c9ab730f5513206b999ec0d90d1fb',
    imageId: 'b4u5c9v9eplflpfjohz8',
    name: 'Singapore',
    displayNameKey: 'singapore',
    url: '/search/hotels?destinationId=le_7cba87f571a31baf11325f462fbdcf14&destinationName=Singapore',
    countryCode: 'SG',
  },
  {
    destinationId: 'le_15fa44474c92da27cf2125373356db4c',
    imageId: 'asgz0fkt6cf311m98rq',
    name: 'Melbourne',
    displayNameKey: 'melbourne',
    url: '/search/hotels?destinationId=le_15fa44474c92da27cf2125373356db4c&destinationName=Melbourne',
    countryCode: 'AU',
  },
  {
    destinationId: 'le_43baa6762fa81bb43b39c62553b2970d',
    imageId: 'eoo7x60icwbgndyc1wcge',
    name: 'Dubai',
    displayNameKey: 'dubai',
    url: '/search/hotels?destinationId=le_43baa6762fa81bb43b39c62553b2970d&destinationName=Dubai',
    countryCode: 'AE',
  },
]

function TripLandingPageTrendingDestinations() {
  const intl = useIntl()
  const localizedDestinations = React.useMemo<Array<CityTrendingDestination>>(() => (
    destinations.map((destination) => ({
      ...destination,
      displayName: intl.formatMessage(destinationNameMessages[destination.displayNameKey]),
    }))
  ), [intl])
  const { finalDestinations, fetchingDestinations } = useTrendingDestinations(localizedDestinations)

  return (
    <SectionContainer gap={32}>
      <LayoutContainer>
        <Heading variant="heading2">
          <FormattedMessage
            id="E2rYzwYIeILjJitolona"
            defaultMessage="Start with a <i>trending destination</i>"
            description="Section heading for trending destinations section in the trip planner"
            values={{
              i: (chunks: React.ReactNode) => <i key="italic">{chunks}</i>,
            }}
          />
        </Heading>
      </LayoutContainer>
      <TripLandingPageTrendingDestinationsCarousel
        fetchingDestinations={fetchingDestinations}
        trendingDestinations={finalDestinations}
      />
    </SectionContainer>
  )
}

export default TripLandingPageTrendingDestinations
